import type { Action } from "redux";
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  merge,
  mergeMap,
  switchMap,
  takeUntil,
} from "rxjs";
import { concat, from, of } from "rxjs";
import type { Epic } from "redux-observable";
import type { RootState } from "@/store/app/storeTypes";
import type { EpicDependencies } from "@/store/epics/epicDependencies";
import {
  channelDetailSubscriptionRequested,
  channelDetailSubscriptionCancelled,
  requestStart,
  requestSuccess,
  REQUEST_KEYS,
} from "@/store/requests/requestsSlice";
import {
  getStatementAffinityForPeriod,
  getStatementHistoryForChannelDetail,
} from "@/features/channelDetail/services/channelDetail.service";
import { getStatementSummaries } from "@/services/statistics.service";
import { STATEMENT_CHANNEL } from "@/constants/statementChannel";
import type { UserProfile } from "@/hooks/useUserProfile";
import { isUserProfileSessionReady } from "@/lib/session/isUserProfileSessionReady";
import { profileFromState } from "@/store/epics/profileFromState";
import { CHANNEL_DETAIL_SUB_KEYS } from "@/features/channelDetail/constants";
import {
  toRequestErrorAction,
  watchPeriodScopedPollStreams,
} from "@/store/epics/createPeriodScopedPollingSubscription";

const REQUEST_KEY = REQUEST_KEYS.CHANNEL_DETAIL;

const channelDetailEpic: Epic<Action, Action, RootState, EpicDependencies> = (
  action$,
  state$,
  { statisticPollingIntervalMs },
) => {
  return action$.pipe(
    filter(
      (a): a is ReturnType<typeof channelDetailSubscriptionRequested> =>
        a.type === channelDetailSubscriptionRequested.type,
    ),
    mergeMap(() => {
      const cancel$ = action$.pipe(
        filter(
          (a): a is ReturnType<typeof channelDetailSubscriptionCancelled> =>
            a.type === channelDetailSubscriptionCancelled.type,
        ),
      );

      const historyOnFi$ = state$.pipe(
        map((state) => profileFromState(state)),
        filter((profile) => isUserProfileSessionReady(profile)),
        distinctUntilChanged(
          (a, b) =>
            a.currentFinancialInstitution === b.currentFinancialInstitution,
        ),
        switchMap((profile) => {
          const userProfile = profile as UserProfile;
          return concat(
            of(
              requestStart({
                requestKey: REQUEST_KEY,
                subKey: CHANNEL_DETAIL_SUB_KEYS.HISTORY,
              }),
            ),
            from(getStatementHistoryForChannelDetail(userProfile)).pipe(
              map((data) =>
                requestSuccess({
                  requestKey: REQUEST_KEY,
                  subKey: CHANNEL_DETAIL_SUB_KEYS.HISTORY,
                  data,
                }),
              ),
              catchError((err: unknown) =>
                of(
                  toRequestErrorAction(
                    REQUEST_KEY,
                    CHANNEL_DETAIL_SUB_KEYS.HISTORY,
                    err,
                  ),
                ),
              ),
            ),
          );
        }),
        takeUntil(cancel$),
      );

      const periodScoped$ = watchPeriodScopedPollStreams({
        state$,
        cancel$,
        intervalMs: statisticPollingIntervalMs,
        streams: [
          {
            requestKey: REQUEST_KEY,
            subKey: CHANNEL_DETAIL_SUB_KEYS.EMAIL,
            fetch: (profile, period) =>
              getStatementSummaries(profile, STATEMENT_CHANNEL.EMAIL, period),
          },
          {
            requestKey: REQUEST_KEY,
            subKey: CHANNEL_DETAIL_SUB_KEYS.DIGITAL,
            fetch: (profile, period) =>
              getStatementSummaries(profile, STATEMENT_CHANNEL.DIGITAL, period),
          },
          // Use the combined (channel=both) request for the total instead of summing
          // email + digital: the per-channel counts overlap, so a member on both
          // channels would count twice. The combined request returns unique members.
          {
            requestKey: REQUEST_KEY,
            subKey: CHANNEL_DETAIL_SUB_KEYS.BOTH,
            fetch: (profile, period) =>
              getStatementSummaries(profile, STATEMENT_CHANNEL.BOTH, period),
          },
          {
            requestKey: REQUEST_KEY,
            subKey: CHANNEL_DETAIL_SUB_KEYS.AFFINITY,
            fetch: (profile, period) =>
              getStatementAffinityForPeriod(profile, period),
          },
        ],
      });

      return merge(historyOnFi$, periodScoped$);
    }),
  );
};

export default channelDetailEpic;
